import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { IconButton, Button } from "@mui/material";
import { TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import CreateIcon from "@mui/icons-material/Create";
import ClearIcon from "@mui/icons-material/Clear";
import VrWatermark from "../../../components/vrWatermark/VrWatermark";

const PREFIX = "ExparienceInformations";

const classes = {
  root: `${PREFIX}-root`,
  marginBottom: `${PREFIX}-marginBottom`,
  watermarkContainer: `${PREFIX}-watermarkContainer`,
  watermarkImage: `${PREFIX}-watermarkImage`,
  paintingContainer: `${PREFIX}-paintingContainer`,
  editImageIcon: `${PREFIX}-editImageIcon`,
  hashtagInput: `${PREFIX}-hashtagInput`,
  mainTitle: `${PREFIX}-mainTitle`,
  topContainer: `${PREFIX}-topContainer`,
  customWatermark: `${PREFIX}-customWatermark`,
  marginMobileBottom: `${PREFIX}-marginMobileBottom`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.between("xs", "md")]: {
      flexDirection: "column",
      minWidth: "100%",
      maxWidth: "100%",
      padding: "1rem",
    },
  },

  [`& .${classes.marginBottom}`]: {
    marginBottom: "1rem",
  },

  [`& .${classes.watermarkContainer}`]: {
    position: "relative",
    maxWidth: "370px",
    marginBottom: "2rem",
    [theme.breakpoints.up("sm")]: {
      width: "100%",
      maxWidth: "650px",
      margin: "0 auto",
      marginBottom: "2rem",
    },
  },

  [`& .${classes.watermarkImage}`]: {
    maxWidth: "370px",
    width: "100%",
    borderRadius: "10px",
  },

  [`& .${classes.paintingContainer}`]: {
    position: "relative",
    maxWidth: "370px",
    margin: "0 auto",
  },

  [`& .${classes.editImageIcon}`]: {
    display: "flex",
    position: "absolute",
    right: "0",
    top: "0",
  },

  [`& .${classes.hashtagInput}`]: {
    maxWidth: "650px",
    margin: "0 auto !important",
  },

  [`& .${classes.mainTitle}`]: {
    [theme.breakpoints.up("sm")]: {
      textAlign: "center",
    },
    marginBottom: "1rem",
  },

  [`& .${classes.topContainer}`]: {
    display: "flex",
    justifyContent: "space-between",
    maxWidth: "370px",
    position: "relative",
    margin: "0 auto 1rem auto",
  },

  [`& .${classes.customWatermark}`]: {
    maxWidth: "650px",
    margin: "0 auto 60px auto",
    width: "100%",
    textAlign: "center",
    marginTop: "1rem",
  },

  [`& .${classes.marginMobileBottom}`]: {
    marginBottom: "4rem",
  },
}));

const ExparienceInformations = (props) => {
  const { t } = useTranslation();
  const [displayCustomIcon, setDisplayCustomIcon] = useState(false);
  const [urls, setUrls] = useState({});

  function handleImageReader(event, id) {
    let tmp = { ...urls };
    tmp[id] = URL.createObjectURL(event.target.files[0]);
    setUrls(tmp);
  }

  useEffect(() => {
    if (props.files.clientLogo) {
      let tmp = { ...urls };
      tmp["clientLogo"] = URL.createObjectURL(props.files.clientLogo[0]);
      setUrls(tmp);
    }
  }, [props.files]);

  useEffect(() => {
    if (props.exparience?.externalLinks?.length > 0) setDisplayCustomIcon(true);
  }, [props.exparience]);

  useEffect(() => {
    return () => {
      const keys = Object.keys(urls);
      keys.forEach((url) => url.revokeObjectURL());
    };
  }, []);

  return (
    <Root>
      <div
        className={clsx(
          classes.root,
          displayCustomIcon ? classes.marginMobileBottom : ""
        )}
      >
        <Typography variant="h5" className={classes.mainTitle}>
          {t("myExpariences.watermark")}
        </Typography>
        <div className={classes.watermarkContainer}>
          <div>
            <div className={classes.imageContainer}>
              <div className={classes.topContainer}>
                <Typography variant="h6">
                  {t("myExpariences.watermarkImage")}
                </Typography>
                {(urls.clientLogo ||
                  (props.exparience && props.exparience.clientLogoUrl)) && (
                  <div className={classes.editImageIcon}>
                    <input
                      type="file"
                      accept="image/*"
                      name="watermarkImage"
                      id="modify-watermark-logo"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        handleImageReader(e, "clientLogo");
                        props.setFiles({
                          ...props.files,
                          clientLogo: e.target.files,
                        });
                        props.setExparience({
                          ...props.exparience,
                          clientLogo: e.target.files[0].name,
                        });
                      }}
                    />
                    <label htmlFor="modify-watermark-logo">
                      <IconButton
                        color="primary"
                        aria-label="upload picture"
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        size="large"
                      >
                        <CreateIcon />
                      </IconButton>
                    </label>
                    <div>
                      <input
                        name="delete-watermark-logo"
                        id="delete-watermark-logo"
                        style={{ display: "none" }}
                      />
                      <label htmlFor="delete-watermark-logo">
                        <IconButton
                          color="primary"
                          aria-label="delete watermark logo"
                          component="span"
                          onClick={(e) => {
                            const tmpFiles = { ...props.files };
                            delete tmpFiles.clientLogo;
                            props.setFiles(tmpFiles);
                            props.setExparience({
                              ...props.exparience,
                              clientLogo: "",
                              clientLogoUrl: "",
                            });
                            const tmpUrls = { ...urls };
                            URL.revokeObjectURL(tmpUrls.clientLogo);
                            delete tmpUrls.clientLogo;
                            setUrls(tmpUrls);
                          }}
                          size="large"
                        >
                          <ClearIcon />
                        </IconButton>
                      </label>
                    </div>
                  </div>
                )}
              </div>
              <div className={classes.paintingContainer}>
                <img
                  id="clientLogo"
                  className={classes.watermarkImage}
                  alt={
                    (urls.clientLogo && urls.clientLogo) ||
                    (props.exparience && props.exparience.clientLogoUrl)
                      ? "client logo"
                      : null
                  }
                  src={
                    (urls.clientLogo ? urls.clientLogo : undefined) ||
                    (props.exparience
                      ? props.exparience.clientLogoUrl
                      : undefined)
                  }
                />
                {!(
                  urls.clientLogo ||
                  (props.exparience && props.exparience.clientLogoUrl)
                ) && (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      name="clientLogo"
                      id="icon-button-client-logo-add"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        handleImageReader(e, "clientLogo");
                        props.setFiles({
                          ...props.files,
                          clientLogo: e.target.files,
                        });
                        props.setExparience({
                          ...props.exparience,
                          clientLogo: e.target.files[0].name,
                        });
                      }}
                    />
                    <label htmlFor="icon-button-client-logo-add">
                      <Button
                        variant="contained"
                        color="primary"
                        component="span"
                      >
                        {t("myDesign.zones.uploadVideo.selectFileButton")}
                      </Button>
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <TextField
          name="hashtag"
          label={t("myExpariences.form.hashtag")}
          className={clsx(classes.hashtagInput, classes.marginBottom)}
          multiline
          value={props.exparience && props.exparience.hashtag}
          onChange={(e) =>
            props.setExparience({
              ...props.exparience,
              hashtag: e.target.value,
            })
          }
          fullWidth
          variant={"outlined"}
        />
        <div className={classes.customWatermark}>
          {!displayCustomIcon && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setDisplayCustomIcon(true)}
              className={classes.downloadQRButton}
            >
              {t("myDesign.webVR.addCustomLink")}
            </Button>
          )}
          {displayCustomIcon && (
            <div className={classes.customWatermark}>
              <VrWatermark
                open={displayCustomIcon}
                externalLinks={
                  props.exparience && props.exparience.externalLinks
                    ? props.exparience.externalLinks
                    : []
                }
                exparience={props.exparience}
                setExparience={props.setExparience}
                isSaveEnabled
              />
            </div>
          )}
        </div>
      </div>
    </Root>
  );
};

export default ExparienceInformations;
